import { FieldValues, Path, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { InputDate } from "alurkerja-ui";
import { RequiredAsterisk } from "./required-asterisk";
import { formatDate } from "@/utils/helper/formatDate";
import { LucideX } from "lucide-react";
import { cn } from "@/utils/helper";

interface FieldInputDateProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: string;
  hideLabel?: boolean;
  description?: string;
  isVisuallyHidden?: boolean;
  hasRequiredAsterisk?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  disabled?: boolean;
  dateFormat?: string;
  showMonthYearPicker?: boolean;
  showFullMonthYearPicker?: boolean;
  clearButtonClassName?: string;
  classNameContainer?: string;
  className?: string;
  placeholder?: string;
  onChange?: (date?: Date | null) => void;
}

export function FieldInputDate<TFormValues extends FieldValues>({
  name,
  label,
  hideLabel,
  description,
  isVisuallyHidden = false,
  hasRequiredAsterisk = false,
  selectsEnd = false,
  selectsStart = false,
  startDate,
  endDate,
  minDate,
  maxDate,
  disabled = false,
  dateFormat = "yyyy-MM-dd",
  showMonthYearPicker = false,
  showFullMonthYearPicker = false,
  classNameContainer,
  className,
  clearButtonClassName,
  placeholder,
  onChange,
}: FieldInputDateProps<TFormValues>) {
  const handleChange = onChange;
  const form = useFormContext();
  const currentDateValue = form.watch(name);

  function handleClear() {
    // @ts-expect-error undefined value
    form.setValue(name, undefined);
    handleChange?.(undefined);
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field: { onChange, value, ...fieldProps } }) => (
        <FormItem
          className={`${
            isVisuallyHidden
              ? "sr-only"
              : cn(
                  "relative w-full break-inside-avoid-column text-black",
                  classNameContainer,
                )
          } ${hideLabel ? " space-y-0" : ""}`}
        >
          {label && (
            <FormLabel className={hideLabel ? "sr-only" : ""}>
              {label}
            </FormLabel>
          )}
          {hasRequiredAsterisk && <RequiredAsterisk />}
          <FormControl>
            <InputDate
              {...fieldProps}
              ref={undefined}
              name={fieldProps.name}
              selected={value ? new Date(value) : null}
              selectsStart={selectsStart}
              selectsEnd={selectsEnd}
              startDate={startDate}
              endDate={endDate}
              minDate={minDate}
              maxDate={maxDate}
              disabled={disabled}
              onChange={(date) => {
                if (date) {
                  const formattedDate = formatDate(date);
                  onChange(formattedDate);
                  handleChange?.(date);
                }
              }}
              dateFormat={dateFormat}
              showMonthYearPicker={showMonthYearPicker}
              showFullMonthYearPicker={showFullMonthYearPicker}
              placeholderText={placeholder}
              className={cn(
                "relative h-11 w-full rounded border border-[#c4c4c480] px-3 py-2 focus-within:border-indigo-600 focus-within:ring-indigo-600 focus:border-indigo-600 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-neutral-100",
                className,
              )}
            />
          </FormControl>
          {!disabled && currentDateValue && (
            <button
              className={cn("absolute right-9 top-9", clearButtonClassName)}
              onClick={handleClear}
            >
              <LucideX size={20} />
              <span className="sr-only">Clear date</span>
            </button>
          )}
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
